import { AxiosError } from 'axios'
import clone from 'just-clone'
import { ThunkAction } from 'redux-thunk'
import {
  BufferGeometry,
  LoadingManager,
  Mesh,
  Texture,
  TextureLoader,
} from 'three'
import { DefaultItems } from '../data/DefaultItems'
import {
  HardwareType,
  MarkupPropsType,
  MarkupType,
  MaterialGroupPropsType,
  MaterialGroupType,
  MaterialPropsType,
  MaterialType,
  addHardwareToDB,
  addMarkupToDB,
  addMaterialGroupToDB,
  addMaterialToDB,
  deleteHardwareFromDB,
  deleteMaterialFromDB,
  deleteMaterialGroupFromDB,
  editHardwareInDB,
  editMarkupInDB,
  editMaterialGroupInDB,
  editMaterialInDB,
  fetchHardwares,
  fetchMarkups,
  fetchMaterialGroups,
  fetchMaterials,
  fetchTextures,
  uploadTextureToServer,
} from './../api/appAPI'
import { HardwareProps } from './../classes/Hardware'
import Item, { ItemPropsType, LinkType } from './../classes/Item'
import {
  EnqueueSnackbarType,
  showMessage,
} from './../components/common/Helpers'
import { getCabinetSizes, getCategories, getConfigs } from './appReducer'
import { InferActionsType, StateType } from './store'

export type AvailableMaterialsForFM = {
  distributor: string
  materials: MaterialType[]
}[]

const initState = {
  hardwaresList: [] as HardwareType[],
  markups: [] as MarkupType[],
  itemsPropsList:
    //  localStorage.getItem('itemsProps')
    //     ? JSON.parse(localStorage.getItem('itemsProps') || '') as ItemPropsType[]
    //     :
    DefaultItems as ItemPropsType[],
  items: [] as Item[],
  openItemsName: [] as string[],
  materials: [] as MaterialType[],
  availableMaterialsForFM: [] as AvailableMaterialsForFM,
  textures: [] as string[],
  materialGroups: [] as MaterialGroupType[],
  cabinetAddonD: 0,
  cabinetAddonP: 0,
}

type InitStateType = typeof initState

const itemReducer = (state = initState, action: ActionsType): InitStateType => {
  switch (action.type) {
    case 'cc/item/SET_HARDWARES':
      return {
        ...state,
        hardwaresList: action.arr,
      }
    case 'cc/item/SET_MARKUPS':
      return {
        ...state,
        markups: action.arr,
      }
    case 'cc/item/SET_MATERIALS':
      return {
        ...state,
        materials: action.arr,
      }
    case 'cc/item/SET_TEXTURES':
      return {
        ...state,
        textures: action.arr,
      }
    case 'cc/item/SET_MATERIAL_GROUPS':
      return {
        ...state,
        materialGroups: action.arr,
      }
    case 'cc/item/SET_ITEMS':
      return {
        ...state,
        items: action.arr,
      }
    case 'cc/item/ADD_ITEMS_PROPS': {
      let itemsPropsList = clone(state.itemsPropsList)

      if (action.forShelves) {
        if (action.arr.length) {
          itemsPropsList = itemsPropsList.filter((props) => {
            if (props.name.slice(0, 5) === 'Shelf') {
              return (
                props.name.split('_')[1] !== action.arr[0].name.split('_')[1]
              )
            }
            return true
          })
        } else {
          itemsPropsList = itemsPropsList.filter((props) => {
            if (props.name.slice(0, 5) === 'Shelf') {
              return props.name.split('_')[1] !== action.shelfSectionNumber
            }
            return true
          })
        }
      }

      return {
        ...state,
        itemsPropsList: [...itemsPropsList, ...action.arr],
      }
    }
    case 'cc/item/ADD_HARDWARE_TO_ITEM': {
      const itemsProps = clone(state.itemsPropsList)

      const itemsNames = action.itemName.split('$$').reverse()

      let item = undefined as undefined | ItemPropsType

      for (let i = 0; i < itemsNames.length; i++) {
        if (i === 0)
          item = itemsProps.find(
            (item) => item.name === itemsNames[i] && item.render
          )
        else if (item)
          item = item.childrenProps.find((item) => item.name === itemsNames[i])

        if (!item) break
      }

      if (item) {
        const lastLocalId = Math.max(
          ...item.hardwaresProps.map((id) => id.localId),
          0
        )
        item.hardwaresProps.push({
          id: action.hId,
          localId: lastLocalId + 1,
          count: 1,
        })
      }

      return {
        ...state,
        itemsPropsList: [...itemsProps],
      }
    }
    case 'cc/item/DELETE_HARDWARE_FROM_ITEM': {
      const itemsProps = clone(state.itemsPropsList)

      const itemsNames = action.itemName.split('$$').reverse()

      let item = undefined as undefined | ItemPropsType

      for (let i = 0; i < itemsNames.length; i++) {
        if (i === 0)
          item = itemsProps.find(
            (item) => item.name === itemsNames[i] && item.render
          )
        else if (item)
          item = item.childrenProps.find((item) => item.name === itemsNames[i])

        if (!item) break
      }

      if (item) {
        item.hardwaresProps = item.hardwaresProps.filter(
          (id) => id.localId !== action.localId
        )
        item.links = item.links.filter(
          (link) => link.hId.split('_').pop() !== action.localId.toString()
        )
      }

      return {
        ...state,
        itemsPropsList: [...itemsProps],
      }
    }
    case 'cc/item/SET_HARDWARE_COUNT': {
      const itemsProps = clone(state.itemsPropsList)

      const itemsNames = action.itemName.split('$$').reverse()

      let item = undefined as undefined | ItemPropsType

      for (let i = 0; i < itemsNames.length; i++) {
        if (i === 0)
          item = itemsProps.find(
            (item) => item.name === itemsNames[i] && item.render
          )
        else if (item)
          item = item.childrenProps.find((item) => item.name === itemsNames[i])

        if (!item) break
      }

      if (item) {
        const hProp = item.hardwaresProps.find(
          (id) => id.localId === action.localId
        )
        if (hProp) hProp.count = action.count
      }

      return {
        ...state,
        itemsPropsList: [...itemsProps],
      }
    }
    case 'cc/item/SET_PARAM_COUNT_HARDWARE_PER_CM': {
      const itemsProps = clone(state.itemsPropsList)

      const itemsNames = action.itemName.split('$$').reverse()

      let item = undefined as undefined | ItemPropsType

      for (let i = 0; i < itemsNames.length; i++) {
        if (i === 0)
          item = itemsProps.find(
            (item) => item.name === itemsNames[i] && item.render
          )
        else if (item)
          item = item.childrenProps.find((item) => item.name === itemsNames[i])

        if (!item) break
      }

      if (item) {
        const link = item.links.find((l) => l.hId === action.localId.toString())
        if (link && link.hProp.split('_')[0] === 'count') {
          link.hProp = 'count_' + action.perCM
        }
      }

      return {
        ...state,
        itemsPropsList: [...itemsProps],
      }
    }
    case 'cc/item/SET_ITEM_ADDON': {
      const itemsProps = clone(state.itemsPropsList)

      const itemsNames = action.itemName.split('$$').reverse()

      let item = undefined as undefined | ItemPropsType

      for (let i = 0; i < itemsNames.length; i++) {
        if (i === 0)
          item = itemsProps.find(
            (item) => item.name === itemsNames[i] && item.render
          )
        else if (item)
          item = item.childrenProps.find((item) => item.name === itemsNames[i])

        if (!item) break
      }

      if (item) {
        item.addon = {
          dollar: action.dollar,
          percentage: action.percentage,
        }
      }

      return {
        ...state,
        itemsPropsList: [...itemsProps],
      }
    }
    case 'cc/item/ADD_OPEN_ITEM':
      return {
        ...state,
        openItemsName: [...state.openItemsName, action.itemName],
      }
    case 'cc/item/REMOVE_OPEN_ITEM':
      return {
        ...state,
        openItemsName: state.openItemsName.filter(
          (name) => name !== action.itemName
        ),
      }
    case 'cc/item/SET_OPEN_ITEM':
      return {
        ...state,
        openItemsName: [action.itemName],
      }
    case 'cc/item/ADD_LINK': {
      const itemsProps = clone(state.itemsPropsList)
      const item = itemsProps.find(
        (item) => item.name === action.itemName && item.render
      )

      if (item) {
        const link = item.links.find(
          (link) =>
            link.hId === action.link.hId && link.hProp === action.link.hProp
        )
        if (link) {
          link.itemProp = action.link.itemProp
        } else item.links.push(action.link)
      }

      return {
        ...state,
        itemsPropsList: [...itemsProps],
      }
    }
    case 'cc/item/SET_ITEMS_PROPS_LIST':
      return {
        ...state,
        itemsPropsList: [...action.arr],
      }
    case 'cc/item/SET_CABINET_ADDON_D':
      return {
        ...state,
        cabinetAddonD: action.value,
      }
    case 'cc/item/SET_CABINET_ADDON_P':
      return {
        ...state,
        cabinetAddonP: action.value,
      }
    case 'cc/item/SET_CABINET_ADDONS':
      return {
        ...state,
        cabinetAddonD: action.d,
        cabinetAddonP: action.p,
      }
    case 'cc/item/SET_AVAILABLE+MATERIALS_FOR_FM':
      return {
        ...state,
        availableMaterialsForFM: action.arr,
      }
    default:
      return state
  }
}

export const actions = {
  setHardwares: (arr: HardwareType[]) =>
    ({ type: 'cc/item/SET_HARDWARES', arr } as const),
  setMarkups: (arr: MarkupType[]) =>
    ({ type: 'cc/item/SET_MARKUPS', arr } as const),
  setMaterials: (arr: MaterialType[]) =>
    ({ type: 'cc/item/SET_MATERIALS', arr } as const),
  setTextures: (arr: string[]) =>
    ({ type: 'cc/item/SET_TEXTURES', arr } as const),
  setMaterialGroups: (arr: MaterialGroupType[]) =>
    ({ type: 'cc/item/SET_MATERIAL_GROUPS', arr } as const),
  setItems: (arr: Item[]) => ({ type: 'cc/item/SET_ITEMS', arr } as const),
  addItemsProps: (
    arr: ItemPropsType[],
    forShelves: boolean = false,
    shelfSectionNumber: string = '-1'
  ) =>
    ({
      type: 'cc/item/ADD_ITEMS_PROPS',
      arr,
      forShelves,
      shelfSectionNumber,
    } as const),
  addHardwareToItem: (itemName: string, hId: number) =>
    ({ type: 'cc/item/ADD_HARDWARE_TO_ITEM', itemName, hId } as const),
  deleteHardwareFromItem: (itemName: string, localId: number) =>
    ({ type: 'cc/item/DELETE_HARDWARE_FROM_ITEM', itemName, localId } as const),
  setOpenItem: (itemName: string) =>
    ({ type: 'cc/item/SET_OPEN_ITEM', itemName } as const),
  addOpenItem: (itemName: string) =>
    ({ type: 'cc/item/ADD_OPEN_ITEM', itemName } as const),
  removeOpenItem: (itemName: string) =>
    ({ type: 'cc/item/REMOVE_OPEN_ITEM', itemName } as const),
  addLink: (itemName: string, link: LinkType) =>
    ({ type: 'cc/item/ADD_LINK', itemName, link } as const),
  setItemsPropsList: (arr: ItemPropsType[]) =>
    ({ type: 'cc/item/SET_ITEMS_PROPS_LIST', arr } as const),
  setItemAddon: (itemName: string, dollar: number, percentage: number) =>
    ({ type: 'cc/item/SET_ITEM_ADDON', itemName, dollar, percentage } as const),
  setHardwareCount: (itemName: string, localId: number, count: number) =>
    ({ type: 'cc/item/SET_HARDWARE_COUNT', itemName, localId, count } as const),
  setParamCountHardwarePerCM: (
    itemName: string,
    localId: number,
    perCM: number
  ) =>
    ({
      type: 'cc/item/SET_PARAM_COUNT_HARDWARE_PER_CM',
      itemName,
      localId,
      perCM,
    } as const),
  setCabinetAddonD: (value: number) =>
    ({ type: 'cc/item/SET_CABINET_ADDON_D', value } as const),
  setCabinetAddonP: (value: number) =>
    ({ type: 'cc/item/SET_CABINET_ADDON_P', value } as const),
  setCabinetAddons: (d: number, p: number) =>
    ({ type: 'cc/item/SET_CABINET_ADDONS', d, p } as const),
  setAvailableMaterialsForFM: (arr: AvailableMaterialsForFM) =>
    ({ type: 'cc/item/SET_AVAILABLE+MATERIALS_FOR_FM', arr } as const),
}

export type ActionsType = InferActionsType<typeof actions>
type ThunkType = ThunkAction<Promise<void>, StateType, unknown, ActionsType>

//Thunks

export const getData = (): ThunkType => async (dispatch) => {
  try {
    dispatch(getConfigs())
    dispatch(getCabinetSizes())
    dispatch(getHardwares())
    dispatch(getMarkups())
    dispatch(getMaterials())
    dispatch(getTextures())
    dispatch(getMaterialGroups())
    dispatch(getCategories())
    // dispatch(getFloorMaterials())
  } catch (error) {}
}

export const getHardwares = (): ThunkType => async (dispatch) => {
  try {
    const hardwares = await fetchHardwares()
    hardwares.reverse()
    dispatch(actions.setHardwares(hardwares))
  } catch (error) {}
}

export const addHardware =
  (
    hardwareProps: HardwareProps,
    enqueueSnackbar: EnqueueSnackbarType
  ): ThunkType =>
  async (dispatch) => {
    try {
      const res = await addHardwareToDB(hardwareProps)

      if (res === 'OK') {
        dispatch(getHardwares())
        showMessage(enqueueSnackbar, 'Part is added', 'success')
      }
    } catch (error) {
      showMessage(enqueueSnackbar, (<AxiosError>error).response?.data)
    }
  }

export const copyHardware =
  (id: number, enqueueSnackbar: EnqueueSnackbarType): ThunkType =>
  async (dispatch, getState) => {
    try {
      const hardwaresList = getState().itemReducer.hardwaresList

      const hardware = hardwaresList.find((h) => h.id === id)

      if (hardware) {
        const { id, ...hardwareProps } = hardware

        let name = (hardwareProps.name += ' Copy')
        let sku = (hardwareProps.sku += ' Copy')

        if (hardwaresList.find((h) => h.name === name)) {
          let i = 2

          name += '(' + i + ')'

          ;(function func() {
            if (hardwaresList.find((h) => h.name === name)) {
              i++
              name = name.slice(0, -3)
              name += '(' + i + ')'
              func()
            }
          })()
        }

        if (hardwaresList.find((h) => h.sku === sku)) {
          let i = 2

          sku += '(' + i + ')'

          ;(function func() {
            if (hardwaresList.find((h) => h.sku === sku)) {
              i++
              sku = sku.slice(0, -3)
              sku += '(' + i + ')'

              func()
            }
          })()
        }

        hardwareProps.name = name
        hardwareProps.sku = sku
        hardwareProps.editable = '1'
        const res = await addHardwareToDB(hardwareProps)

        if (res === 'OK') {
          dispatch(getHardwares())
          showMessage(enqueueSnackbar, 'Part is copied', 'success')
        }
      }
    } catch (error) {
      showMessage(enqueueSnackbar, (<AxiosError>error).response?.data)
    }
  }

export const editHardware =
  (hardware: HardwareType, enqueueSnackbar: EnqueueSnackbarType): ThunkType =>
  async (dispatch) => {
    try {
      await editHardwareInDB(hardware)

      showMessage(enqueueSnackbar, 'Part changed', 'success')

      dispatch(getHardwares())
    } catch (error) {
      showMessage(enqueueSnackbar, (<AxiosError>error).response?.data)
    }
  }

export const deleteHardware =
  (id: number, enqueueSnackbar: EnqueueSnackbarType): ThunkType =>
  async (dispatch) => {
    try {
      const res = await deleteHardwareFromDB(id)

      if (res === 'OK') {
        showMessage(enqueueSnackbar, 'Part deleted', 'success')
        dispatch(getHardwares())
      }
    } catch (error) {
      showMessage(enqueueSnackbar, (<AxiosError>error).response?.data)
    }
  }

export const getMarkups = (): ThunkType => async (dispatch) => {
  try {
    const markups = await fetchMarkups()
    markups.reverse()
    dispatch(actions.setMarkups(markups))
  } catch (error) {}
}

export const addMarkup =
  (
    markupProps: MarkupPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): ThunkType =>
  async (dispatch) => {
    try {
      const res = await addMarkupToDB(markupProps)

      if (res === 'OK') {
        showMessage(enqueueSnackbar, 'Markup added', 'success')
        dispatch(getMarkups())
      }
    } catch (error) {
      showMessage(enqueueSnackbar, (<AxiosError>error).response?.data)
    }
  }

export const editMarkup =
  (
    id: number,
    markupProps: MarkupPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): ThunkType =>
  async (dispatch) => {
    try {
      await editMarkupInDB(id, markupProps)

      showMessage(enqueueSnackbar, 'Markup changed', 'success')
      dispatch(getMarkups())
      dispatch(getHardwares())
    } catch (error) {
      showMessage(enqueueSnackbar, (<AxiosError>error).response?.data)
    }
  }

export const getMaterials = (): ThunkType => async (dispatch, getState) => {
  try {
    const materialsProps = await fetchMaterials()
    materialsProps.reverse()

    const materials = [] as MaterialType[]

    for (const mProps of materialsProps) {
      const getTexture = () =>
        new Promise<Texture | undefined>(async (resolve, reject) => {
          if (mProps.texture) {
            const manager = new LoadingManager()
            const loader = new TextureLoader(manager)
            // console.log(process.env.PUBLIC_URL);

            // const texture = loader.load(process.env.PUBLIC_URL + '/textures/' + mProps.texture)
            const texture = loader.load(
              process.env.REACT_APP_TEXTURE_FOLDER_URL + mProps.texture
            )
            manager.onLoad = () => {
              let colorText = mProps.description.split('}')[0]

              if (mProps.description.includes('}') && colorText[0] === '{') {
                colorText = colorText.slice(1).trim()
                texture.name = 'color||' + colorText
              }

              resolve(texture)
            }
            manager.onError = (e) => {
              console.error(e)
            }
          } else resolve(undefined)
        })

      const texture = await getTexture()

      materials.push({
        ...mProps,
        texture: {
          url: mProps.texture,
          texture,
        },
      })
    }
    // const imgUrl = await import('../assets/textures/test.jpg')
    // // @ts-ignore
    // new ImageLoader().load(imgUrl.default, data => {
    //     console.log('data')
    //     console.log(data)
    // }, () => { }, e => {
    //     console.log(e)
    // })

    dispatch(actions.setMaterials(materials))

    const user = getState().appReducer.user
    const company = getState().appReducer.company
    const materialsForFloor = getState().itemReducer.materials.filter(
      (m) => m.type === 'floor'
    )

    const distributors = new Set()
    let availableMaterialsForFM = []

    for (const material of materialsForFloor) {
      distributors.add(material.distributorCompany)
    }

    for (const dis of Array.from(distributors)) {
      availableMaterialsForFM.push({
        distributor: dis as string,
        materials: materialsForFloor.filter(
          (m) => m.distributorCompany === dis
        ),
      })
    }

    if (!user) return

    if (user.role === 'admin') {
    } else if (!company || user.role === 'none') return
    else {
      //company

      let companyDistributors = [] as string[]

      try {
        const options = JSON.parse(company.options)

        if (options.distributorCompanies) {
          companyDistributors = options.distributorCompanies.split(',')
        }
      } catch (error) {
        console.error('invalid company options - ', company.options)
      }

      availableMaterialsForFM = availableMaterialsForFM.filter((_) =>
        companyDistributors.includes(_.distributor)
      )
    }

    dispatch(actions.setAvailableMaterialsForFM(availableMaterialsForFM))
  } catch (error) {
    console.log(error)
  }
}

const acceptedTextureFormats = ['jpg', 'jpeg', 'jfif', 'png', 'webp']

export const getTextures = (): ThunkType => async (dispatch) => {
  try {
    const textures = await fetchTextures()

    const filteredTextures = textures.filter((t) =>
      acceptedTextureFormats.includes(t.split('.')[t.split('.').length - 1])
    )

    dispatch(actions.setTextures(filteredTextures))
  } catch (error) {
    console.log(error)
  }
}

export const addMaterial =
  (
    materialProps: MaterialPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): ThunkType =>
  async (dispatch) => {
    try {
      const res = await addMaterialToDB(materialProps)

      if (res === 'OK') {
        showMessage(enqueueSnackbar, 'Material added', 'success')
        dispatch(getMaterials())
      }
    } catch (error) {
      showMessage(enqueueSnackbar, (<AxiosError>error).response?.data)
    }
  }

export const copyMaterial =
  (id: number, enqueueSnackbar: EnqueueSnackbarType): ThunkType =>
  async (dispatch, getState) => {
    try {
      const materials = getState().itemReducer.materials

      const material = materials.find((m) => m.id === id)

      if (material) {
        const { id, ...mProps } = material

        const materialProps = {
          ...mProps,
          texture: mProps.texture.url,
        }

        let name = (materialProps.name += ' Copy')
        // let sku = materialProps.sku += ' Copy'

        if (materials.find((m) => m.name === name)) {
          let i = 2

          name += '(' + i + ')'

          ;(function func() {
            if (materials.find((m) => m.name === name)) {
              i++
              name = name.slice(0, -3)
              name += '(' + i + ')'
              func()
            }
          })()
        }
        // if (materials.find(m => m.sku === sku)) {
        //     let i = 2

        //     sku += '(' + i + ')';

        //     (function func() {
        //         if (materials.find(m => m.sku === sku)) {
        //             i++
        //             sku = sku.slice(0, -3)
        //             sku += '(' + i + ')'

        //             func()
        //         }
        //     })()
        // }

        materialProps.name = name
        // materialProps.sku = sku
        materialProps.editable = '1'

        const res = await addMaterialToDB(materialProps)

        if (res === 'OK') {
          showMessage(enqueueSnackbar, 'Material copied', 'success')
          dispatch(getMaterials())
        }
      }
    } catch (error) {
      showMessage(enqueueSnackbar, (<AxiosError>error).response?.data)
    }
  }

export const editMaterial =
  (
    id: number,
    material: MaterialPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): ThunkType =>
  async (dispatch) => {
    try {
      await editMaterialInDB(id, material)

      showMessage(enqueueSnackbar, 'Material changed', 'success')

      dispatch(getMaterials())
    } catch (error) {
      showMessage(enqueueSnackbar, (<AxiosError>error).response?.data)
    }
  }

export const deleteMaterial =
  (id: number, enqueueSnackbar: EnqueueSnackbarType): ThunkType =>
  async (dispatch) => {
    try {
      const res = await deleteMaterialFromDB(id)

      if (res === 'OK') {
        showMessage(enqueueSnackbar, 'Material deleted', 'success')
        dispatch(getHardwares())
        dispatch(getMaterials())
      }
    } catch (error) {
      showMessage(enqueueSnackbar, (<AxiosError>error).response?.data)
    }
  }

export const uploadTexture =
  (
    name: string,
    newTexture: string,
    enqueueSnackbar: EnqueueSnackbarType
  ): ThunkType =>
  async (dispatch) => {
    try {
      await uploadTextureToServer(name, newTexture)

      showMessage(enqueueSnackbar, 'Texture uploaded', 'success')

      dispatch(getTextures())
    } catch (error) {
      showMessage(enqueueSnackbar, (<AxiosError>error).response?.data)
    }
  }

export const getMaterialGroups = (): ThunkType => async (dispatch) => {
  try {
    const mGroups = await fetchMaterialGroups()
    mGroups.reverse()
    dispatch(actions.setMaterialGroups(mGroups))
  } catch (error) {}
}

export const addMaterialGroup =
  (
    mGroupProps: MaterialGroupPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): ThunkType =>
  async (dispatch) => {
    try {
      const res = await addMaterialGroupToDB(mGroupProps)

      if (res === 'OK') {
        showMessage(enqueueSnackbar, 'Material Group added', 'success')
        dispatch(getMaterialGroups())
      }
    } catch (error) {
      showMessage(enqueueSnackbar, (<AxiosError>error).response?.data)
    }
  }

export const copyMaterialGroup =
  (id: number, enqueueSnackbar: EnqueueSnackbarType): ThunkType =>
  async (dispatch, getState) => {
    try {
      const materialGroups = getState().itemReducer.materialGroups

      const materialGroup = materialGroups.find((m) => m.id === id)

      if (materialGroup) {
        const { id, ...materialGroupProps } = materialGroup

        let name = (materialGroupProps.name += ' Copy')

        if (materialGroups.find((m) => m.name === name)) {
          let i = 2

          name += '(' + i + ')'

          ;(function func() {
            if (materialGroups.find((m) => m.name === name)) {
              i++
              name = name.slice(0, -3)
              name += '(' + i + ')'
              func()
            }
          })()
        }

        materialGroupProps.name = name
        // materialGroupProps.editable = '1'

        const res = await addMaterialGroupToDB(materialGroupProps)

        if (res === 'OK') {
          showMessage(enqueueSnackbar, 'Material Group copied', 'success')
          dispatch(getMaterialGroups())
        }
      }
    } catch (error) {
      showMessage(enqueueSnackbar, (<AxiosError>error).response?.data)
    }
  }

export const editMaterialGroup =
  (
    id: number,
    mGroup: MaterialGroupPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): ThunkType =>
  async (dispatch) => {
    try {
      await editMaterialGroupInDB(id, mGroup)

      showMessage(enqueueSnackbar, 'Material Group changed', 'success')

      dispatch(getMaterialGroups())
    } catch (error) {
      showMessage(enqueueSnackbar, (<AxiosError>error).response?.data)
    }
  }

export const deleteMaterialGroup =
  (id: number, enqueueSnackbar: EnqueueSnackbarType): ThunkType =>
  async (dispatch) => {
    try {
      const res = await deleteMaterialGroupFromDB(id)

      if (res === 'OK') {
        showMessage(enqueueSnackbar, 'Material Group deleted', 'success')
        dispatch(getHardwares())
        dispatch(getMaterialGroups())
      }
    } catch (error) {
      showMessage(enqueueSnackbar, (<AxiosError>error).response?.data)
    }
  }

export default itemReducer
